import * as React from 'react';
import type { PressEvent } from 'react-aria';

import { Button } from '../Button';
import { Description } from '../Description';
import { Heading } from '../Heading';
import { ProgressiveImage, type ProgressiveImageProps } from '../Image';
import { LoadingPanel } from '../Loading';
import { TextInput } from '../TextInput';
import type { IconDefinition } from '../util';
import {
    AlertDialog,
    DialogActionButton,
    type DialogActionButtonProps,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
} from './Dialog';
import { DialogIconLayout, type DialogIconLayoutProps } from './DialogIconLayout';

export type UseConfirmInput = {
    title: React.ReactNode;
    cancelLabel?: string | null;
    /**
     * Reduce the spacing around the image and content.
     */
    compact?: boolean;
    confirmLabel: string;
    confirmLeadingIcon?: IconDefinition;
    onConfirm: DialogActionButtonProps['onPress'];
    description?: React.ReactNode | undefined;
    /**
     * Image displayed above the dialog title.
     * Accepts the same props as `ProgressiveImage`, including themed light/dark sources.
     */
    image?: ProgressiveImageProps | undefined;
    icon?: DialogIconLayoutProps['icon'] | undefined;
    kind?: DialogActionButtonProps['kind'];
    onCancel?: () => void;
    children?: React.ReactNode;
};

export type ConfirmEntry = {
    key: string;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    confirmed: boolean;
    disabled?: boolean;
} & Omit<UseConfirmInput, 'onCancel'>;

/**
 * The delay to clear the confirm modals that are closed.
 */
const CLEAR_DELAY = 1000;

let $entries: ConfirmEntry[] = [];

const listeners = new Set<() => void>();
const setConfirms: React.Dispatch<React.SetStateAction<ConfirmEntry[]>> = (prevOrEntries) => {
    $entries = typeof prevOrEntries === 'function' ? prevOrEntries($entries) : prevOrEntries;
    listeners.forEach((listener) => listener());
};

/**
 * Confirm Provider that renders all the confirm modals.
 */
export function ConfirmProvider(props: { children: React.ReactNode }) {
    const entries = React.useSyncExternalStore(
        (onStoreChange) => {
            listeners.add(onStoreChange);
            return () => {
                listeners.delete(() => setConfirms($entries));
            };
        },
        () => $entries
    );

    React.useEffect(() => {
        const closedConfirms = entries.filter((confirm) => !confirm.isOpen);

        // Clear the outdated confirms to avoid memory leaks.
        if (closedConfirms.length) {
            const timeout = setTimeout(() => {
                setConfirms((prev) => prev.filter((confirm) => confirm.isOpen));
            }, CLEAR_DELAY);
            return () => clearTimeout(timeout);
        }
    }, [entries]);

    return (
        <>
            {props.children}
            {entries.map((entry) => {
                const header = (
                    <DialogHeader compact={entry.compact}>
                        <Heading>{entry.title}</Heading>
                        {entry.description ? <Description>{entry.description}</Description> : null}
                    </DialogHeader>
                );
                return (
                    <DialogRoot
                        key={entry.key}
                        isOpen={entry.isOpen}
                        onOpenChange={entry.onOpenChange}
                    >
                        <AlertDialog
                            className={
                                entry.compact
                                    ? '[&_[data-spine-dialog-dismiss]]:top-1 [&_[data-spine-dialog-dismiss]]:right-1 md:[&_[data-spine-dialog-dismiss]]:top-1.5 md:[&_[data-spine-dialog-dismiss]]:right-1.5'
                                    : undefined
                            }
                        >
                            <React.Suspense
                                fallback={
                                    <>
                                        <DialogIconLayout icon={entry.icon}>
                                            {entry.image ? (
                                                <ConfirmDialogImage
                                                    image={entry.image}
                                                    compact={entry.compact}
                                                />
                                            ) : null}
                                            {header}
                                            <DialogBody
                                                className={
                                                    entry.compact
                                                        ? 'px-4! py-4! md:px-6! md:py-6!'
                                                        : undefined
                                                }
                                            >
                                                <LoadingPanel size="small" />
                                            </DialogBody>
                                        </DialogIconLayout>
                                        <DialogFooter>
                                            {entry.cancelLabel !== null ? (
                                                <DialogCloseButton label={entry.cancelLabel} />
                                            ) : null}
                                            <Button
                                                label={entry.confirmLabel}
                                                leadingIcon={entry.confirmLeadingIcon}
                                                kind={entry.kind}
                                                disabled={entry.disabled}
                                            />
                                        </DialogFooter>
                                    </>
                                }
                            >
                                <DialogIconLayout icon={entry.icon}>
                                    {entry.image ? (
                                        <ConfirmDialogImage
                                            image={entry.image}
                                            compact={entry.compact}
                                        />
                                    ) : null}
                                    {header}
                                    {entry.children ? (
                                        <DialogBody
                                            className={
                                                entry.compact
                                                    ? 'px-4! py-4! md:px-6! md:py-6!'
                                                    : undefined
                                            }
                                        >
                                            {entry.children}
                                        </DialogBody>
                                    ) : null}
                                </DialogIconLayout>
                                <DialogFooter>
                                    {entry.cancelLabel !== null ? (
                                        <DialogCloseButton label={entry.cancelLabel} />
                                    ) : null}
                                    <DialogActionButton
                                        kind={entry.kind}
                                        label={entry.confirmLabel}
                                        leadingIcon={entry.confirmLeadingIcon}
                                        onPress={(event, options) => {
                                            const promise = entry.onConfirm(event, options);
                                            if (promise) {
                                                return promise.then(() => {
                                                    entry.confirmed = true;
                                                });
                                            }
                                            entry.confirmed = true;
                                        }}
                                        disabled={entry.disabled}
                                    />
                                </DialogFooter>
                            </React.Suspense>
                        </AlertDialog>
                    </DialogRoot>
                );
            })}
        </>
    );
}

function ConfirmDialogImage(props: { image: ProgressiveImageProps; compact?: boolean }) {
    const { image, compact } = props;

    return (
        <div
            className={compact ? '-mb-2 px-4 pt-4 md:px-6 md:pt-6' : 'px-4 pt-6 md:px-10 md:pt-10'}
        >
            <div className="overflow-hidden rounded-lg bg-muted">
                <ProgressiveImage
                    {...image}
                    className={['block w-full bg-muted', image.className].filter(Boolean).join(' ')}
                />
            </div>
        </div>
    );
}

/**
 * Hook to get a confirm function to show a confirm modal.
 */
export function confirm(input: UseConfirmInput) {
    const { onCancel, ...props } = input;
    return new Promise<boolean>((resolve) => {
        const key = Math.random().toString(36).slice(2, 9);
        const close = () =>
            setConfirms((prev) => {
                return prev.map((confirm) => {
                    if (confirm.key === key) {
                        return { ...confirm, isOpen: false };
                    }
                    return confirm;
                });
            });
        setConfirms((prev) => {
            const entry = {
                key,
                isOpen: true,
                onOpenChange: (isOpen) => {
                    if (!isOpen) {
                        if (!entry.confirmed) {
                            input.onCancel?.();
                        }
                        resolve(entry.confirmed);
                        close();
                    }
                },
                confirmed: false,
                ...props,
            };
            return [...prev, entry];
        });
    });
}

export type UseInputProps = Omit<UseConfirmInput, 'onConfirm'> & {
    onConfirm?: (value: string) => void | Promise<void>;
};

export function input(
    props: UseInputProps,
    {
        initialValue = '',
        validate,
        inputProps,
    }: {
        initialValue?: string;
        validate?: (value: string) => string | null;
        inputProps?: React.ComponentProps<typeof TextInput>;
    }
): Promise<string | undefined> {
    return new Promise((resolve) => {
        const key = Math.random().toString(36).slice(2, 9);
        let inputValue = initialValue;

        const close = () =>
            setConfirms((prev) => {
                return prev.map((confirm) => {
                    if (confirm.key === key) {
                        return { ...confirm, isOpen: false };
                    }
                    return confirm;
                });
            });

        setConfirms((prev) => {
            const entry: ConfirmEntry = {
                key,
                isOpen: true,
                onOpenChange: (isOpen) => {
                    if (!isOpen) {
                        if (!entry.confirmed) {
                            props.onCancel?.();
                        }
                        resolve(entry.confirmed ? inputValue : undefined);
                        close();
                    }
                },
                confirmed: false,
                disabled: Boolean(validate?.(initialValue)),
                title: props.title,
                cancelLabel: props.cancelLabel,
                confirmLabel: props.confirmLabel,
                compact: props.compact,
                confirmLeadingIcon: props.confirmLeadingIcon,
                description: props.description,
                image: props.image,
                icon: props.icon,
                onConfirm: async () => {
                    const promise = props.onConfirm?.(inputValue);
                    if (promise) {
                        await promise;
                    }
                    entry.confirmed = true;
                },
                children: (
                    <InputField
                        key={key}
                        initialValue={initialValue}
                        validate={validate}
                        inputProps={inputProps}
                        onValueChange={(value) => {
                            inputValue = value;
                        }}
                        entryKey={key}
                        onSubmit={() => {
                            const error = validate?.(inputValue);
                            if (!error) {
                                entry.onConfirm({} as PressEvent, { preventClose: () => {} });
                            }
                        }}
                    />
                ),
            };
            return [...prev, entry];
        });
    });
}

/* InputField component for managing the `input` dialog's input field */
function InputField({
    initialValue,
    validate,
    inputProps,
    onValueChange,
    entryKey,
    onSubmit,
}: {
    initialValue: string;
    validate?: (value: string) => string | null;
    inputProps?: React.ComponentProps<typeof TextInput>;
    onValueChange: (value: string) => void;
    entryKey: string;
    onSubmit: () => void;
}) {
    const [value, setValue] = React.useState(initialValue);

    // Sync local input state with parent dialog state and update validation
    React.useEffect(() => {
        onValueChange(value);
        const error = validate?.(value);
        setConfirms((prev) =>
            prev.map((entry) =>
                entry.key === entryKey ? { ...entry, disabled: Boolean(error) } : entry
            )
        );
    }, [value, validate, onValueChange, entryKey]);

    return (
        <TextInput
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onSubmit={onSubmit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    onSubmit();
                }
            }}
            feedback={validate?.(value) ? { type: 'danger', message: validate(value) } : undefined}
            {...inputProps}
        />
    );
}
